import React, { useEffect, useState } from "react";

interface PaymentTimerProps {
  seconds?: number;
  onComplete: () => void;
  onCancel?: () => void;
}

const PaymentTimer: React.FC<PaymentTimerProps> = ({
  seconds = 5,
  onComplete,
  onCancel,
}) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [progress, setProgress] = useState(100);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const handleCancel = () => {
    setIsCancelled(true);
    setTimeLeft(0);
    setProgress(0);
    if (!!onCancel) {
      onCancel();
    }
  };

  useEffect(() => {
    if (timeLeft <= 0 && !isCancelled) {
      // Countdown finished — swap the static ring for a spinner while the
      // async place-order calls run so the user sees continued activity.
      setIsComplete(true);
      onComplete();
      return () => {};
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      setProgress((prev) => (prev > 0 ? prev - 100 / seconds : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, isCancelled, onComplete, seconds]);

  useEffect(() => {
    // Cancel the order if the user leaves the page.
    // Note: beforeunload callbacks must be synchronous — async calls are not
    // guaranteed to complete before the page unloads. We call the provided
    // onCancel synchronously here; the parent is responsible for any cleanup
    // that needs to persist (e.g. cancel-order via sendBeacon).
    const handleUnload = () => {
      if (timeLeft > 0 && !!onCancel) {
        onCancel();
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [onCancel, timeLeft]);

  return (
    <div className="relative w-32 h-32">
      {/* Circle Timer — spins once the countdown completes and the order is
          being placed; counts down as a shrinking arc before that. */}
      <svg
        className={`w-full h-full ${isComplete ? "animate-spin" : "-rotate-90"}`}
        viewBox="0 0 100 100"
      >
        <circle
          cx="50"
          cy="50"
          r="44"
          stroke="#d5bda0"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r="44"
          stroke="#8b4513"
          strokeWidth="8"
          fill="none"
          strokeDasharray={isComplete ? "70 206" : "276"}
          strokeDashoffset={isComplete ? 0 : ((100 - progress) / 100) * 276}
          strokeLinecap="round"
          className={isComplete ? "" : "transition-all duration-1000"}
        />
      </svg>

      {/* Time Display */}
      {!!onCancel && !isComplete && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <span className="text-2xl font-bold playfair-text text-navy">
            {timeLeft}
          </span>
          <span className="text-xs opensans-text text-gray-400 uppercase tracking-wide">
            sec
          </span>
          <button
            onClick={handleCancel}
            className="mt-1 text-xs opensans-text text-rust hover:text-rust-dark underline underline-offset-2 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default PaymentTimer;
